import { useMemo, useState } from "react";
import { Table } from "../Table/Table";
import { TableColumn } from "../Table/Table.types";
import { KioskPerformanceSummaryRow } from "../../types/kioskTransactions.type";
import { formatCurrency } from "../../utils/currencyUtils";
import { formatDate } from "../../utils/dateFormatter";

interface KioskPerformanceSummaryTableProps {
  rows: KioskPerformanceSummaryRow[];
  isKioskOwner?: boolean;
  loading?: boolean;
  error?: string | null;
}

export const KioskPerformanceSummaryTable = ({
  rows,
  isKioskOwner = false,
  loading,
  error,
}: KioskPerformanceSummaryTableProps) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const columns = useMemo<TableColumn<KioskPerformanceSummaryRow>[]>(() => {
    const baseColumns: TableColumn<KioskPerformanceSummaryRow>[] = [
      { id: "kioskId", label: "Kiosk ID", priority: "high" },
      { id: "kioskName", label: "Kiosk Name", priority: "high" },
      {
        id: "kioskLocation",
        label: "Location",
        priority: "medium",
        format: (value) => value || "-",
      },
      {
        id: "successfulTransactions",
        label: "Successful",
        align: "right",
        priority: "medium",
      },
      {
        id: "totalReceivedBase",
        label: "Received (USD)",
        align: "right",
        priority: "high",
        format: (value) => formatCurrency(value, "USD"),
      },
      { id: "totalUnits", label: "Units", align: "right", priority: "medium" },
      {
        id: "lastTransactionAt",
        label: "Last Transaction",
        priority: "low",
        format: (value) => (value ? formatDate(value) : "-"),
      },
    ];

    if (!isKioskOwner) {
      baseColumns.splice(4, 0, {
        id: "failedTransactions",
        label: "Failed",
        align: "right",
        priority: "medium",
      });
    }

    return baseColumns;
  }, [isKioskOwner]);

  const totalPages = Math.max(1, Math.ceil(rows.length / Math.max(1, limit)));

  const pagedRows = useMemo(
    () => rows.slice((page - 1) * limit, page * limit),
    [rows, page, limit],
  );

  const canGoPrev = page > 1;
  const canGoNext = page < totalPages;

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Kiosk Performance Summary
        </h2>
        <div className="text-sm text-gray-600 dark:text-gray-300">
          {rows.length} records
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <Table
        data={pagedRows}
        columns={columns}
        loading={loading}
        emptyMessage="No kiosk summary records found"
        rowKey={(row) => row.kioskId}
      />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-1">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">Rows per page</span>
          <select
            value={limit}
            onChange={(event) => {
              const nextLimit = Number(event.target.value);
              setLimit(nextLimit);
              setPage(1);
            }}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-slate-800 text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={!canGoPrev}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 text-sm disabled:opacity-60"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={!canGoNext}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 text-sm disabled:opacity-60"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default KioskPerformanceSummaryTable;
